/**
 * API client for the public Proceeding Digest (the /proceedings/{docket}/digest page).
 * Backend: GET /api/proceedings/{docket}/digest  (see legal-app-api/comment_digest.py)
 * Returns auto-discovered key issues + camps + cited positions, most-cited
 * authorities, filer roster, and a filer<->issue graph for the topic map.
 */

function getBaseUrl(): string {
  const raw = (import.meta as any)?.env?.VITE_RAG_API_BASE_URL as string | undefined;
  const fallback = 'https://fcc-summarize-app-r4ac3.ondigitalocean.app';
  return (raw || fallback).replace(/\/+$/, '');
}

export interface DigestPosition {
  filer: string;
  summary: string;
  stance?: string; // this filer's stance on the issue: support | oppose | modify | mixed
  pages?: number[]; // page(s) the filer's stance is grounded in
  page?: number | null; // legacy single-page (older cached digests)
}

export interface DigestIssue {
  /** URL-safe id for the per-topic drill-down page route. */
  id?: string;
  title: string;
  desc: string;
  /** The take-able claim that support/oppose are measured against (v2+). */
  proposition?: string;
  n_filers: number;
  n_positions: number;
  stance: Record<string, number>;
  /** Top filers shown on the digest overview. */
  positions: DigestPosition[];
  /** EVERY filer who engaged this issue — populated for the per-topic page. */
  all_positions?: DigestPosition[];
}

export interface DigestAuthority {
  citation: string;
  n_filers: number;
}

export interface DigestGraphNode {
  id: string;
  label: string;
  group: 'issue' | 'filer';
  color?: string;
  value?: number;
  title?: string;
}

export interface DigestGraphEdge {
  from: string;
  to: string;
  color?: string;
}

export interface ExpressTheme { label: string; n: number; stance: string; }
export interface ExpressRepresentative { excerpt: string; count: number; stance: string; }
export interface ExpressVelocity { week: string; n: number; }

/** Aggregate sentiment over a docket's mass "express" (web-form) comments — the
 *  public-opinion layer the substantive issue analysis deliberately excludes.
 *  Only present for dockets above the express-volume gate. */
export interface ExpressSentiment {
  n_express: number;                       // EXACT total (ECFS aggregation)
  n_fetched?: number;                      // comments pulled for the analysis sample
  n_classified?: number;                   // distinct versions sent to the classifier
  direct_pct?: number;                     // % known exactly (form letters); rest estimated
  sampled?: boolean;                       // n_fetched < n_express
  capped?: boolean;
  n_unique_variants: number;
  n_analyzed?: number;                     // legacy (older cached digests)
  coverage_pct?: number;                   // legacy
  sentiment: Record<string, number>;       // representative estimate (counts)
  sentiment_pct: Record<string, number>;   // support/oppose/other %
  themes: ExpressTheme[];
  representative: ExpressRepresentative[];
  velocity: ExpressVelocity[];
  as_of: string;
}

export interface ProceedingDigest {
  docket: string;
  status: 'ok' | 'insufficient_data';
  /** Build date (YYYY-MM-DD) for the "Updated as of" stamp. */
  built_at?: string;
  /** True when there were too few substantive filings for issues, but enough
   *  express comments to publish a public-sentiment-only digest. */
  express_only?: boolean;
  /** Public express-comment sentiment (viral/mass-comment dockets). */
  express_sentiment?: ExpressSentiment;
  /** Why a digest couldn't be built (only set when status==='insufficient_data'):
   *  'no_analyzable_comments' — we analyzed the filings but found nothing substantive;
   *  'too_few_positions' — fewer than the minimum distinct positions. */
  reason?: string;
  version?: string;
  cached?: boolean;
  n_positions: number;
  n_filers: number;
  issues: DigestIssue[];
  authorities: DigestAuthority[];
  filers: string[];
  graph: { nodes: DigestGraphNode[]; edges: DigestGraphEdge[] };
}

/** Returns the digest, or null if none has been built yet (404). Throws on real errors. */
export async function fetchProceedingDigest(docket: string): Promise<ProceedingDigest | null> {
  const url = `${getBaseUrl()}/api/proceedings/${encodeURIComponent(docket)}/digest`;
  const res = await fetch(url, { cache: 'no-store' });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Digest request failed (${res.status})`);
  return (await res.json()) as ProceedingDigest;
}

export interface DigestBuildState {
  state: 'ready' | 'building' | 'analyzing' | 'queued' | 'no_comments' | 'needs_ingest';
  count?: number;
}

/** Ask the server to build a digest for this proceeding (the "Generate digest"
 *  button). Resolves with {state}; throws Error(message) on 409 (not analyzed / at
 *  capacity) so the UI can show why. */
export async function requestProceedingDigest(docket: string): Promise<DigestBuildState> {
  const url = `${getBaseUrl()}/api/proceedings/${encodeURIComponent(docket)}/digest/request`;
  const res = await fetch(url, { method: 'POST' });
  if (res.status === 409) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail || 'This proceeding can’t be digested right now.');
  }
  if (!res.ok) throw new Error(`Digest request failed (${res.status})`);
  return (await res.json()) as DigestBuildState;
}
